import { RadioGroup, Radio } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/solid";
import clsx from "clsx";
import {
  addLocalNotification,
  clearNewMainBusiness,
  requestAddMainBusiness,
  requestListBusiness,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import { useEffect, useMemo, useState } from "react";
import { sortBusinessesForSwitch } from "../../../../Constants/business";
import Modal from "../../Modal";
import Button from "../../Button";
import BusinessAvatar from "../BusinessAvatar";
import { useTranslation } from "react-i18next";

const BusinessSwitchModal = ({ open, onClose }) => {
  const { t } = useTranslation();

  const [selected, setSelected] = useState();

  const dispatch = useBusinessTransactionDispatch();
  const authDispatch = useAuthDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { businesses, loading } = useBusinessTransactionState();
  const {
    user,
    mainBusinessStatus,
    loading: authLoading,
  } = useAuthState();

  const isLoading = loading || authLoading;

  // Current business first, then active ones, restricted/pending last; name asc
  // within each group.
  const sortedBusinesses = useMemo(
    () =>
      sortBusinessesForSwitch(
        businesses,
        user?.mainBusinessID || user?.mainBusiness?.id,
      ),
    [businesses, user?.mainBusinessID, user?.mainBusiness?.id],
  );

  useEffect(() => {
    if (user?.id) {
      requestListBusiness(dispatch, { owner: user.id });
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (mainBusinessStatus) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `switch-business-${mainBusinessStatus}`,
        type: "success",
        title: "common.success",
        body: "businesses.notifications.switchSuccess",
        showFull: true,
      });
      // Clear reducer
      clearNewMainBusiness(authDispatch);
    }
  }, [authDispatch, mainBusinessStatus, notificationDispatch, onClose]);

  // Pass the whole record, not just the id: the list query carries the
  // lifecycle fields the write gates read, the REST switch response may not.
  const setMainBusiness = (business) => {
    requestAddMainBusiness(authDispatch, {
      businessID: business.id,
      business,
    });
  };

  const radioBaseStyle =
    "relative block rounded-lg border border-gray-300 bg-white px-6 py-4 shadow-sm";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="businesses.switch"
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!Boolean(selected?.id)}
            loading={isLoading}
            onClick={() => {
              setMainBusiness(selected);
            }}
          >
            {t("common.switch")}
          </Button>
        </div>
      }
    >
      <fieldset aria-label={t("businesses.switch")}>
        <RadioGroup
          value={selected}
          onChange={setSelected}
          className="space-y-4"
        >
          {isLoading
            ? [...Array(3)].map((_, i) => (
                <div key={i} className={radioBaseStyle}>
                  <BusinessAvatar loading />
                </div>
              ))
            : sortedBusinesses.map((business) => (
                <Radio
                  key={business.id}
                  value={business}
                  aria-label={business.name}
                  className={clsx(
                    radioBaseStyle,
                    "group cursor-pointer focus:outline-none data-[focus]:border-blue-600 data-[focus]:ring-2 data-[focus]:ring-blue-600 sm:flex sm:justify-between"
                  )}
                >
                  <BusinessAvatar business={business} />
                  <CheckCircleIcon
                    aria-hidden="true"
                    className="h-5 w-5 text-blue-600 [.group:not([data-checked])_&]:invisible"
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-blue-600"
                  />
                </Radio>
              ))}
        </RadioGroup>
      </fieldset>
    </Modal>
  );
};

export default BusinessSwitchModal;
